import { createContext, useContext, useState, useEffect } from 'react'
import { 
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  getMultiFactorResolver,
  TotpMultiFactorGenerator
} from 'firebase/auth'
import { auth, adminAuth } from '../config/firebase'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [mfaResolver, setMfaResolver] = useState(null)

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log('🔑 Auth state changed:', user ? user.email : 'null')
      setUser(user)
      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  const signUp = async (email, password) => {
    setError(null)
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password)
      return result.user
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  const signIn = async (email, password) => {
    setError(null)
    try {
      const result = await signInWithEmailAndPassword(auth, email, password)
      return result.user
    } catch (err) {
      if (err.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, err)
        setMfaResolver(resolver)
        throw err
      }
      setError(err.message)
      throw err
    }
  }

  const signInWithGoogle = async () => {
    setError(null)
    console.log('🚀 Starting Google popup...')
    try {
      const provider = new GoogleAuthProvider()
      const result = await signInWithPopup(auth, provider)
      console.log('✅ SUCCESS:', result.user.email)

      const isNewUser = result.user.metadata.creationTime === result.user.metadata.lastSignInTime
      return { user: result.user, isNewUser }
    } catch (err) {
      console.error('❌ FULL ERROR:', err)
      console.error('❌ Code:', err.code)
      console.error('❌ Message:', err.message)
      
      if (err.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, err)
        setMfaResolver(resolver)
      }
      
      setError(err.message)
      throw err
    }
  }

  // Complete MFA sign-in with TOTP code
  const completeMfaSignIn = async (totpCode) => {
    if (!mfaResolver) {
      throw new Error('No MFA resolver available')
    }

    try {
      // Get the TOTP hint (first enrolled TOTP factor)
      const totpHint = mfaResolver.hints.find(
        hint => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID
      )

      if (!totpHint) {
        throw new Error('No TOTP factor enrolled')
      }

      const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
        totpHint.uid,
        totpCode
      )

      const result = await mfaResolver.resolveSignIn(multiFactorAssertion)
      setMfaResolver(null)
      return result.user
    } catch (err) {
      console.error('MFA verification error:', err)
      setError(err.message)
      throw err
    }
  }

  const cancelMfa = () => {
    setMfaResolver(null)
    setError(null)
  }

  const logout = async () => {
    setError(null)
    try {
      await signOut(auth)
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  const resetPassword = async (email) => {
    setError(null)
    try {
      await sendPasswordResetEmail(auth, email)
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  // Admin-only: Create a new user without affecting the current admin session
  // Uses secondary Firebase Auth instance to avoid signing out the admin
  const createUserAsAdmin = async (email, password) => {
    try {
      const result = await createUserWithEmailAndPassword(adminAuth, email, password)
      return result.user
    } catch (err) {
      throw err
    }
  }

  // Admin-only: Send password reset email to a prospect
  // Uses primary auth instance (doesn't matter which for sending reset emails)
  const sendPasswordResetToUser = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email)
    } catch (err) {
      throw err
    }
  }

  const clearError = () => setError(null)

  const value = {
    user,
    loading,
    error,
    mfaResolver,
    signUp,
    signIn,
    signInWithGoogle,
    completeMfaSignIn,
    cancelMfa,
    logout,
    resetPassword,
    createUserAsAdmin,
    sendPasswordResetToUser,
    clearError,
    isAuthenticated: !!user,
    isMfaPending: !!mfaResolver
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
